/**
 * DateRangePicker (spec 016 — shared Reports scaffolding)
 *
 * Shared date-range control for all /reports/* pages. Presets (Last 7 / 30 /
 * 90 days) plus a Custom mode with explicit from/to date inputs. Emits UTC
 * ISO-8601 strings suitable for the `from`/`to` query params on the admin
 * report endpoints. Default range: last 30 days.
 *
 * Kept generic on purpose — six later report pages reuse this component.
 */

import { useState } from 'react';

export interface DateRange {
  /** ISO-8601 UTC range start. */
  from: string;
  /** ISO-8601 UTC range end. */
  to: string;
}

export type DateRangePreset = 7 | 30 | 90;

export const DATE_RANGE_PRESETS: readonly DateRangePreset[] = [7, 30, 90] as const;

/**
 * Compute the range for a "Last N days" preset as UTC ISO strings.
 * `to` is `now`; `from` is exactly N days earlier.
 */
export function presetRange(days: DateRangePreset, now: Date = new Date()): DateRange {
  const to = now.toISOString();
  const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString();
  return { from, to };
}

/** Default range used by report pages before the user picks anything: last 30 days. */
export function defaultDateRange(now: Date = new Date()): DateRange {
  return presetRange(30, now);
}

/**
 * Compute the prior equal-length period immediately preceding `range`
 * (US-17-GR-28). `prior.to` is exactly `range.from` (no gap, no overlap);
 * `prior.from` is `range.from` minus the same duration as `range`.
 */
export function priorPeriodRange(range: DateRange): DateRange {
  const fromMs = new Date(range.from).getTime();
  const toMs = new Date(range.to).getTime();
  const duration = toMs - fromMs;
  return {
    from: new Date(fromMs - duration).toISOString(),
    to: range.from,
  };
}

/**
 * Convert a `<input type="date">` value ("yyyy-MM-dd") to a UTC ISO string.
 * `endOfDay` maps to 23:59:59.999 UTC so the `to` bound is inclusive of the day.
 * Returns null for empty/invalid input.
 */
export function dateInputToUtcIso(value: string, endOfDay: boolean): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  const ms = endOfDay
    ? Date.UTC(y, m - 1, d, 23, 59, 59, 999)
    : Date.UTC(y, m - 1, d, 0, 0, 0, 0);
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

/** ISO string → "yyyy-MM-dd" (UTC) for pre-filling the custom date inputs. */
function isoToDateInput(iso: string): string {
  return iso.slice(0, 10);
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  /**
   * Opt-in "Compare to previous period" toggle (US-17-GR-28). Prop-gated so
   * existing callers are unaffected — the toggle only renders when true.
   */
  showCompareToggle?: boolean;
  /**
   * Fires whenever the compare toggle flips, or the range changes while the
   * toggle is on. Receives the prior equal-length range, or null when
   * compare is off. Only meaningful when `showCompareToggle` is true.
   */
  onCompareChange?: (compareRange: DateRange | null) => void;
}

export function DateRangePicker({ value, onChange, showCompareToggle = false, onCompareChange }: DateRangePickerProps) {
  const [activePreset, setActivePreset] = useState<DateRangePreset | 'custom'>(30);
  const [customFrom, setCustomFrom] = useState(() => isoToDateInput(value.from));
  const [customTo, setCustomTo] = useState(() => isoToDateInput(value.to));
  const [customError, setCustomError] = useState<string | null>(null);
  const [compareOn, setCompareOn] = useState(false);

  const emitCompare = (range: DateRange, enabled: boolean) => {
    if (!showCompareToggle || !onCompareChange) return;
    onCompareChange(enabled ? priorPeriodRange(range) : null);
  };

  const applyPreset = (days: DateRangePreset) => {
    setActivePreset(days);
    setCustomError(null);
    const range = presetRange(days);
    setCustomFrom(isoToDateInput(range.from));
    setCustomTo(isoToDateInput(range.to));
    onChange(range);
    emitCompare(range, compareOn);
  };

  const applyCustom = (fromInput: string, toInput: string) => {
    const from = dateInputToUtcIso(fromInput, false);
    const to = dateInputToUtcIso(toInput, true);
    if (!from || !to) {
      setCustomError(null);
      return;
    }
    if (from > to) {
      setCustomError('From date must be on or before the To date.');
      return;
    }
    setCustomError(null);
    const range = { from, to };
    onChange(range);
    emitCompare(range, compareOn);
  };

  const toggleCompare = () => {
    setCompareOn((prev) => {
      const next = !prev;
      emitCompare(value, next);
      return next;
    });
  };

  const pillClass = (active: boolean) =>
    [
      'px-3 py-1.5 text-xs font-medium rounded-lg transition-colors min-h-[48px]',
      active ? 'bg-violet-700 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200',
    ].join(' ');

  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Date range">
      {DATE_RANGE_PRESETS.map((days) => (
        <button
          key={days}
          type="button"
          aria-pressed={activePreset === days}
          className={pillClass(activePreset === days)}
          onClick={() => applyPreset(days)}
        >
          Last {days} days
        </button>
      ))}
      <button
        type="button"
        aria-pressed={activePreset === 'custom'}
        className={pillClass(activePreset === 'custom')}
        onClick={() => setActivePreset('custom')}
      >
        Custom
      </button>
      {activePreset === 'custom' && (
        <div className="flex items-center gap-2">
          <label className="text-xs text-zinc-500">
            From{' '}
            <input
              type="date"
              aria-label="From date"
              value={customFrom}
              onChange={(e) => {
                setCustomFrom(e.target.value);
                applyCustom(e.target.value, customTo);
              }}
              className="bg-zinc-800 text-zinc-200 text-xs rounded-lg px-2 py-1.5 border border-zinc-700"
            />
          </label>
          <label className="text-xs text-zinc-500">
            To{' '}
            <input
              type="date"
              aria-label="To date"
              value={customTo}
              onChange={(e) => {
                setCustomTo(e.target.value);
                applyCustom(customFrom, e.target.value);
              }}
              className="bg-zinc-800 text-zinc-200 text-xs rounded-lg px-2 py-1.5 border border-zinc-700"
            />
          </label>
        </div>
      )}
      {activePreset === 'custom' && customError && (
        <p role="alert" className="w-full text-xs text-red-400">
          {customError}
        </p>
      )}
      {/*
        Work item 17434: the range is bounded on the UTC day and every server
        metric is computed in UTC, so the zone is stated rather than implied.
        Without it an operator west of Greenwich reads an edge row stamped the
        previous day as a wrong number instead of a zone difference.
      */}
      <span className="text-xs text-zinc-500" data-testid="date-range-timezone">
        Range and timestamps in <abbr title="Coordinated Universal Time">UTC</abbr>
      </span>
      {showCompareToggle && (
        <label className="flex items-center gap-2 text-xs text-zinc-400 ml-1">
          <input
            type="checkbox"
            aria-label="Compare to previous period"
            checked={compareOn}
            onChange={toggleCompare}
            className="rounded border-zinc-700 bg-zinc-800 text-violet-600 focus:ring-violet-500 focus:ring-offset-0"
          />
          Compare to previous period
        </label>
      )}
    </div>
  );
}

export default DateRangePicker;
